import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { WorksTitleBar } from '../components/home/works/WorksTitleBar';
import { HOME_TOPBAR_H } from '../components/home/layout/HomeTopBar';
import { WaterfallView } from '../components/home/works/WaterfallView';
import { VideoView } from '../components/home/works/VideoView';
import { FooterBar } from '../components/home/layout/FooterBar';
import { layoutWaterfall, layoutGrid, GAP, type WorkCard } from '../components/home/works/workLayout';
import { useWorksStore } from '../stores/worksStore';


/**
 * Bong作品独立页（原首页「Bong精选」锚点区升级为独立路由 /bongworks）
 * - 内容：用户上传/发布的全部作品（worksStore.publishedWorks，localStorage 持久化，
 *   publishWork 前插保证最新在前）；首页精选板块恢复为纯模板内容，不再混入用户作品
 * - 样式：完整复用首页精选板块体系——TAB 行 + WorksTitleBar 吸顶（top = HOME_TOPBAR_H）、
 *   图像瀑布流（WaterfallView）/ 视频网格（VideoView）、裁剪窗口 1260 + 「加载更多」、
 *   列数断点（≥1060 → 4 列 / ≥760 → 3 列 / ≥460 → 2 列 / 其余 1 列）均与首页一致
 * - 卡高基准（340 宽坐标系）：图像卡跟随封面真实宽高比（尺寸未知时兜底 452，限幅 200–680），
 *   瀑布流按 colW/340 等比换算，错落感与首页图像精选一致；
 *   视频卡 = 封面 192×colW/340 + 78 文字区（等高网格行序）
 * - 空态：对应 TAB 无作品时显示引导文案（编辑器发布后作品归入本页）
 */
type WorksTab = 'image' | 'video';

const TABS: { key: WorksTab; label: string }[] = [
  { key: 'image', label: '图像作品' },
  { key: 'video', label: '视频作品' },
];

const USER_CARD_H = 452; // 图像作品卡基准高（340 宽坐标系）
const VIDEO_CELL_H = 270; // 视频作品卡基准高（封面 192 + 文字区 78）

interface BongWorksPageProps {
  /** 点击作品卡 → 预览页（图像 188:19 / 视频 202:501），由 AppNew 接管视图切换 */
  onOpenWork?: (card: WorkCard) => void;
}

type PlacedLayout = { cards: (WorkCard & { x: number; y: number; w: number; h: number })[]; canvasH: number };

/* 封面自然尺寸运行时测量：未存尺寸的作品（旧记录/演示数据）补测宽高，
   供瀑布流卡高按真实宽高比换算（发布时已存尺寸的作品不走此兜底） */
const useCoverSizes = (sources: string[]) => {
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>({});
  const pendingRef = useRef<Set<string>>(new Set());
  const key = sources.join('\n');
  useLayoutEffect(() => {
    key
      .split('\n')
      .filter(Boolean)
      .forEach((src) => {
        if (pendingRef.current.has(src)) return;
        pendingRef.current.add(src);
        const img = new Image();
        img.onload = () =>
          setSizes((prev) => ({ ...prev, [src]: { w: img.naturalWidth, h: img.naturalHeight } }));
        img.src = src;
      });
  }, [key]);
  return sizes;
};

const BongWorksPageComponent: React.FC<BongWorksPageProps> = ({ onOpenWork }) => {
  const publishedWorks = useWorksStore((s) => s.publishedWorks);
  const [tab, setTab] = useState<WorksTab>('image');
  // 「加载更多」展开态（两 TAB 独立）：展开后画布完整显示、按钮隐藏（无更多内容）
  const [imageExpanded, setImageExpanded] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  // 标题栏状态提升：与 WorksSection 同款受控（chip/搜索交互）
  const [activeChip, setActiveChip] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const handleTab = useCallback((next: WorksTab) => () => setTab(next), []);

  /* 用户作品按类型拆分为展示卡（store 前插顺序保留 = 最新在前）。
     图像卡高跟随封面真实宽高比（340 宽坐标系，限幅 200–680 防极端长图），
     错落感与首页图像精选瀑布流一致；尺寸未知时暂用基准高 452 */
  const imageWorks = useMemo(() => publishedWorks.filter((w) => w.type === 'image'), [publishedWorks]);
  const coverSizes = useCoverSizes(imageWorks.map((w) => w.coverUrl || w.url));
  const imageCards = useMemo<WorkCard[]>(
    () =>
      imageWorks.map((w) => {
        const src = w.coverUrl || w.url;
        let ratio: number | null = null;
        if (w.coverWidth && w.coverHeight) ratio = w.coverHeight / w.coverWidth;
        else if (coverSizes[src]) ratio = coverSizes[src].h / coverSizes[src].w;
        const h = ratio !== null ? Math.min(680, Math.max(200, Math.round(340 * ratio))) : USER_CARD_H;
        return {
          key: `pub-${w.id}`,
          id: w.id,
          src,
          h,
          title: w.title,
          user: w.authorName,
          avatar: w.authorAvatar,
          likes: String(w.likes),
        };
      }),
    [imageWorks, coverSizes]
  );
  const videoCards = useMemo<WorkCard[]>(
    () =>
      publishedWorks
        .filter((w) => w.type === 'video')
        .map((w) => ({
          key: `pub-${w.id}`,
          id: w.id,
          src: w.coverUrl || '',
          h: VIDEO_CELL_H,
          title: w.title,
          user: w.authorName,
          avatar: w.authorAvatar,
          likes: String(w.likes),
          videoUrl: w.url,
          showFlow: w.isPublicWorkflow,
        })),
    [publishedWorks]
  );

  /* —— 即梦式响应式（与首页 WorksSection 同规则）：实测内容列宽 → 列数断点 →
     列宽 = (容器宽 − 12×(列数−1)) / 列数，卡片随列宽伸缩、字号恒定 —— */
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentW, setContentW] = useState(1400);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setContentW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* 吸附态检测（与首页 WorksSection 同款）：吸顶条正前方 1px 文档流哨兵，以「顶栏高度」
     为观测线（rootMargin 负顶边）——未与顶栏相接前背景透明，相接后显示 #121212 底色 */
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);
  useLayoutEffect(() => {
    const el = stickySentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setStuck(!entries[0].isIntersecting),
      { rootMargin: `-${HOME_TOPBAR_H}px 0px 0px 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const cols = contentW >= 1060 ? 4 : contentW >= 760 ? 3 : contentW >= 460 ? 2 : 1;
  const colW = (contentW - GAP * (cols - 1)) / cols;

  /* 图像作品布局：瀑布流（依次放入当前最矮列，卡高按 colW/340 等比换算） */
  const imageLayout = useMemo<PlacedLayout>(() => {
    if (!imageCards.length) return { cards: [], canvasH: 0 };
    const { placed, canvasH } = layoutWaterfall(imageCards, cols, colW);
    const byKey = new Map(imageCards.map((c) => [c.key, c]));
    return {
      cards: placed.map((p) => ({ ...(byKey.get(p.key) as WorkCard), x: p.x, y: p.y, w: p.w, h: p.h })),
      canvasH,
    };
  }, [imageCards, cols, colW]);

  /* 视频作品布局：等高网格行序（cellH = 封面 192×colW/340 + 78 文字区） */
  const videoLayout = useMemo<PlacedLayout>(() => {
    if (!videoCards.length) return { cards: [], canvasH: 0 };
    const cellH = Math.round((192 * colW) / 340) + 78;
    const { placed, canvasH } = layoutGrid(videoCards.length, cols, colW, cellH);
    return {
      cards: videoCards.map((c, i) => ({ ...c, x: placed[i].x, y: placed[i].y, w: placed[i].w, h: placed[i].h })),
      canvasH,
    };
  }, [videoCards, cols, colW]);

  const isImage = tab === 'image';
  const activeLayout = isImage ? imageLayout : videoLayout;
  const isExpanded = isImage ? imageExpanded : videoExpanded;
  const handleExpand = useCallback(() => {
    if (isImage) setImageExpanded(true);
    else setVideoExpanded(true);
  }, [isImage]);

  return (
    <div className="w-full min-h-screen text-zinc-100" style={{ backgroundColor: '#121212' }}>
      <main className="relative w-full">
        <div className="absolute inset-0 noise-bg pointer-events-none" />
        {/* 内容列 1400 居中（与首页精选板块同列体系）；固定顶栏 72px 高（HOME_TOPBAR_H），内容让位后起排 */}
        <div ref={contentRef} className="relative mx-auto w-full max-w-[1400px]" style={{ paddingTop: HOME_TOPBAR_H + 24 }}>
          {/* 页头：独立页标识 + 作品总数 */}
          <div className="flex items-baseline gap-3 pb-6 pt-2">
            <h1 className="text-[24px] font-semibold leading-[24px] text-white">Bong作品</h1>
            <span className="text-[13px] leading-[13px] text-zinc-500">{publishedWorks.length} 个作品</span>
          </div>

          {/* TAB 行 + 标题栏：原生 sticky 吸顶（top = 顶栏高度），与首页精选板块同构；
              哨兵未滚过顶栏下缘前背景透明，与顶栏相接后显示 #121212
              遮住身后滚过的卡片。pb-3 与下方 mt-3 合计 24px，保持「标题栏 → 卡片」设计稿间距 */}
          <div ref={stickySentinelRef} className="h-px" />
          <div
            className={`sticky z-20 pt-3 pb-3 ${stuck ? 'bg-[#121212]' : 'bg-transparent'}`}
            style={{ top: HOME_TOPBAR_H }}
          >
            <div className="flex h-[18px] items-center gap-6">
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={handleTab(key)}
                  className="cursor-pointer text-[18px] leading-[18px] text-white transition-opacity"
                  style={{ opacity: tab === key ? 1 : 0.45 }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="mt-6">
              <WorksTitleBar
                variant={isImage ? 'waterfall' : 'video'}
                activeChip={activeChip}
                onChipChange={setActiveChip}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
              />
            </div>
          </div>

          {/* 卡片区：24px 间距；无作品时显示空态引导 */}
          <div className="mt-3">
            {activeLayout.cards.length ? (
              <>
                {/* 裁剪窗口：默认高 1260（画布不足时贴合画布高），点击「加载更多」展开至画布全高 */}
                <div
                  className="overflow-hidden"
                  style={{
                    height: isExpanded ? activeLayout.canvasH : Math.min(1260, activeLayout.canvasH),
                    transition: 'height 300ms ease',
                  }}
                >
                  {isImage ? (
                    <WaterfallView
                      expanded={isExpanded}
                      cards={activeLayout.cards}
                      canvasH={activeLayout.canvasH}
                      onOpen={onOpenWork}
                    />
                  ) : (
                    <VideoView
                      expanded={isExpanded}
                      cards={activeLayout.cards}
                      canvasH={activeLayout.canvasH}
                      onOpen={onOpenWork}
                    />
                  )}
                </div>
                {/* 「加载更多」：仅画布超出裁剪窗口（还有被裁内容）时显示；按钮底 → 分隔线 106px */}
                {!isExpanded && activeLayout.canvasH > 1260 && (
                  <div className="mt-[62px] flex justify-center">
                    <button
                      type="button"
                      onClick={handleExpand}
                      className="h-[30px] cursor-pointer rounded-full border border-[#3D3D3D] px-3 text-[14px] leading-[14px] text-white transition-colors hover:border-[#5A5A5A] hover:bg-white/5"
                    >
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-[360px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                <p className="text-[15px] leading-[15px] text-zinc-400">
                  暂无{isImage ? '图像' : '视频'}作品
                </p>
                <p className="mt-2 text-[13px] leading-[13px] text-zinc-600">
                  在编辑器中完成创作并发布后，作品将展示在这里
                </p>
              </div>
            )}
            <div className="mb-[106px]" />
          </div>

          <FooterBar />
        </div>
      </main>
    </div>
  );
};

export const BongWorksPage = memo(BongWorksPageComponent);
